import { AuthService } from '@/features/auth/services/auth-service';
import { sessionHintService } from '@/features/auth/services/session-hint-service';
import { tokenRefreshManager } from '@/features/auth/services/token-refresh-manager';
import { tokenService } from '@/features/auth/services/token-service';
import { getTrackingService } from '@/lib/tracking';
import type { AuthStore, AuthUser } from '@/types';
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';

/**
 * Zustand store for authentication state management
 * Clean store that only handles state, delegates business logic to AuthService
 */
export const useAuthStore = create<AuthStore>()(
  subscribeWithSelector(
    devtools(
      (set, get) => ({
        // Initial state
        isAuthenticated: false,
        isLoading: false,
        error: null,
        user: null,
        isInitialized: false,
        isInitializing: false,
        sessionExpired: false,

        setAuthenticated: (authUser: AuthUser) => {
          set({ isAuthenticated: true, error: null, user: authUser });
        },

        updateUser: (partial: Partial<AuthUser>) => {
          const current = get().user;
          if (current) {
            set({ user: { ...current, ...partial } });
          }
        },

        clearAuth: (sessionExpired = false) => {
          // Stop refresh timers immediately, before any async work
          tokenRefreshManager.stopAutoRefresh();
          // Clear session hint so the next visit won't show "session expired"
          sessionHintService.clear();
          // Fire-and-forget: server logout is best-effort; local state is cleared regardless
          void tokenService.logout();
          set({
            isAuthenticated: false,
            error: null,
            user: null,
            isInitializing: false,
            sessionExpired,
          });

          // Reset user identity in tracking service
          getTrackingService().reset();
        },

        setLoading: (loading: boolean) => {
          set({ isLoading: loading });
        },

        setError: (error: string | null) => {
          set({ error });
        },

        initializeAuth: async () => {
          const state = get();
          // Guard #1: Prevent concurrent initialization
          if (state.isInitializing) {
            return;
          }

          // Guard #2: Prevent re-initialization
          if (state.isInitialized) {
            return;
          }

          set({ isInitializing: true, isLoading: true, error: null });

          try {
            const result = await AuthService.initialize();

            if (result.success && result.user) {
              set({ isAuthenticated: true, error: null, user: result.user, isLoading: false });

              // Set expiry for refresh manager if available
              if (result.expiresIn) {
                tokenRefreshManager.setExpiresIn(result.expiresIn);
              }
            } else {
              tokenRefreshManager.stopAutoRefresh();
              if (sessionHintService.exists() || result.sessionExpired) {
                void tokenService.logout();
              }
              set({
                isAuthenticated: false,
                isLoading: false,
                error: result.error || null,
                sessionExpired: result.sessionExpired ?? false,
              });
            }
          } catch (error) {
            // Unexpected error — still clean up
            tokenRefreshManager.stopAutoRefresh();
            if (sessionHintService.exists()) {
              void tokenService.logout();
            }
            set({
              isAuthenticated: false,
              isLoading: false,
              error:
                error instanceof Error ? error.message : 'Authentication initialization failed',
            });
          } finally {
            set({ isInitializing: false, isInitialized: true });
          }
        },
        resetAuth: () => {
          set({
            isAuthenticated: false,
            isLoading: false,
            error: null,
            user: null,
            isInitialized: false,
            isInitializing: false,
            sessionExpired: false,
          });
        },
      }),
      {
        name: 'auth-store', // Name for devtools
      }
    )
  )
);

// Selector hooks for common use cases
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useAuthError = () => useAuthStore((state) => state.error);
export const useAuthUser = () => useAuthStore((state) => state.user);
export const useCompanyName = () => useAuthStore((state) => state.user?.companyName);
export const useTeamMemberCount = () => useAuthStore((state) => state.user?.teamMemberCount);

// Action hooks
// Provide stable action references without causing extra subscription churn
// Avoid potential infinite subscription loops by deriving actions from store API once
export const useAuthActions = () => {
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated);
  const updateUser = useAuthStore((s) => s.updateUser);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const setLoading = useAuthStore((s) => s.setLoading);
  const setError = useAuthStore((s) => s.setError);
  const initializeAuth = useAuthStore((s) => s.initializeAuth);
  return { setAuthenticated, updateUser, clearAuth, setLoading, setError, initializeAuth } as const;
};
